// eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-unsafe-assignment
const newrelic = require('newrelic'); // must be required first

import { NextPage } from 'next';
import { useTranslation, WithTranslation } from 'react-i18next';
import { Store } from 'redux';
import { LogicMiddleware } from 'redux-logic';
import Head from 'next/head';
import { useCallback, useState } from 'react';

import Layout from 'modules/layout';
import { loadPro, proSelector } from 'modules/users/duck';
import { getCaseStudiesByUserId, getAllCaseStudiesByUserId } from 'modules/marketing-pages/duck';
import ProProfileContent from 'modules/users/modules/profiles/components/ProProfileContent';
import ProfileClaim from 'modules/users/modules/profiles/components/ProfileClaim';
import { getFAQStructuredData } from 'modules/marketing-pages/components/LandingPage/LandingPage/LandingPage';
import { RootState, wrapper } from 'store/index';
import { setCachedStates } from 'store/actions';
import { setupPageAndLoadTranslations } from 'utils/page';
import type {
  BlogPost,
  SeoObject,
  ProLocalBusiness,
} from 'modules/marketing-pages/marketing-page.types';
import {
  JobProfile,
  Profile,
  QuestionAnswer,
  EnabledJobTypesSelectorProps,
  ReviewContent,
} from 'src/modules/users/modules/profiles/types';
import { caseStudiesTotalCountSelector } from 'modules/marketing-pages/duck/selectors';

import FeatureFlagsService from 'src/services/feature-flags';
import JobTypeAliasesService from 'src/services/job-type-alias';
import ProProfileService from 'src/services/pro-profile';
import { getSSRCache, setSSRCache } from 'src/ssr-cache';
import { getStructuredLocalBusinessForPro } from 'modules/marketing-pages/utils';
import { getInternalBlogPostsByJobTypeId } from 'modules/marketing-pages/service';
import { BIG_5_JOB_TYPE_LIST } from 'constants/vertical-lp';
import { SLUG_CASE_STUDIES, SLUG_REVIEWS } from 'constants/pro-profile';

const featureFlagsService = FeatureFlagsService.getInstance();
const jobTypeAliasesService = JobTypeAliasesService.getInstance();
const proProfileService = ProProfileService.getInstance();
const MIN_CASES = 3;

interface Props extends WithTranslation {
  featureFlags: { isReviewsQAEnabled: boolean };
  internalBlogPosts: Array<BlogPost>;
  internalBlogCostPosts: Array<BlogPost>;
  jobProfiles: Array<JobProfile>;
  jobTypeId?: string;
  jobTypeTitle?: string;
  questionAnswers?: Array<QuestionAnswer>;
  seo: SeoObject;
  localBusiness: ProLocalBusiness;
  slugFromQueryParam: string;
  title: string;
}

// title for service profile page
const getJobTypeTitle = (jobTypeId: string, jobProfiles: Array<JobProfile>) => {
  const jobType = jobProfiles.find((item) => item.jobTypeId === jobTypeId);
  return jobType?.label ?? '';
};

const ProProfilePage: NextPage<Props> = ({
  featureFlags,
  internalBlogPosts,
  internalBlogCostPosts,
  jobProfiles,
  jobTypeId: jobTypeIdProp,
  jobTypeTitle,
  questionAnswers,
  seo,
  localBusiness,
  slugFromQueryParam,
  title,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [pageTitle, setPageTitle] = useState(jobTypeTitle ? `${title} | ${jobTypeTitle}` : title);

  const setJobTypeTitle = useCallback(
    (jobTypeIdParam?: string) => {
      if (jobTypeIdParam) {
        const jobTypeName = getJobTypeTitle(jobTypeIdParam, jobProfiles);
        setPageTitle(`${title}${jobTypeName ? ` | ${jobTypeName}` : ''}`);
      } else {
        setPageTitle(title);
      }
    },
    [jobProfiles, title]
  );

  return (
    <Layout
      language={language}
      seo={{
        ...seo,
        ogDescription: t('proProfile.ogDescription'),
      }}
      title={pageTitle}
    >
      <Head>
        <script
          type="application/ld+json"
          key="structured-review-data"
          dangerouslySetInnerHTML={{
            __html: getStructuredLocalBusinessForPro({ ...localBusiness, name: pageTitle }),
          }}
        />
        {questionAnswers && questionAnswers.length > 0 && (
          <script
            type="application/ld+json"
            id="faq-structured-data"
            dangerouslySetInnerHTML={{
              __html: getFAQStructuredData(questionAnswers),
            }}
          />
        )}
      </Head>
      <div className="pro-profile-page-container">
        <ProfileClaim />
        <ProProfileContent
          featureFlags={featureFlags}
          internalBlogPosts={internalBlogPosts}
          internalBlogCostPosts={internalBlogCostPosts}
          jobTypeId={jobTypeIdProp}
          slugFromQueryParam={slugFromQueryParam}
          setJobTypeTitle={setJobTypeTitle}
        />
      </div>
      <style jsx>
        {`
          .pro-profile-page-container {
            height: 100%;
            background: #f5f5f5;
          }
        `}
      </style>
    </Layout>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ req, defaultLocale, locale, query }) => {
      const { dispatch, getState, logicMiddleware } = store as Store<RootState> & {
        logicMiddleware: LogicMiddleware;
      };

      const cacheKeyProps = {
        locale,
        pageName: 'pro-profile',
        query,
        url: req.url ?? '',
      };
      const cachedProps = await getSSRCache(cacheKeyProps, 'props');
      const cachedStates = await getSSRCache(cacheKeyProps, 'states');

      if (cachedProps && cachedStates) {
        dispatch(setCachedStates(cachedStates as RootState));
        await logicMiddleware.whenComplete();
        return { props: cachedProps };
      }

      const { jobTypeIdParam = null, userId } = query as {
        jobTypeIdParam: string | null;
        userId: string;
      };

      // using URL /profile/{SLUG}/pro/case-studies for case-studies page.
      const jobTypeId = jobTypeIdParam === SLUG_CASE_STUDIES ? '' : jobTypeIdParam;

      const { currentLocale, i18nData } = await setupPageAndLoadTranslations({
        defaultLocale,
        locale,
        namespaces: ['common', 'marketingPages', 'request', 'users', 'form'],
        // @ts-expect-error Extended store
        store,
      });

      dispatch(loadPro(userId, 'proProfile'));
      await logicMiddleware.whenComplete();
      const pro = proSelector(getState()) as
        | {
            converted?: boolean;
            deactivated?: boolean;
            enabledJobTypes: Array<EnabledJobTypesSelectorProps>;
            id: string;
            isItp?: boolean;
            jobProfiles?: Array<JobProfile>;
            location?: {
              name: string;
              city?: { name: string };
              prefecture?: { name: string };
              townArea?: { name: string };
              zipCode: string;
            };
            name: string;
            proId?: string;
            proProfile: JobProfile;
            profile: Profile;
            reviews?: {
              counts: Array<number>;
              list: Array<ReviewContent>;
              total: number;
            };
            seoDescription?: string;
            slug?: string;
          }
        | undefined;

      if (!pro || pro.deactivated) {
        return { notFound: true };
      }

      // location.city and location.prefecture should always exist
      // However, we have some bad data where they don't.
      // While we figure it out, we cast it as nullable and log whenever it's missing
      if (!pro.location?.city || !pro.location.prefecture) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        newrelic.addCustomAttributes({
          errorCodes: 'PRO_MISSING_LOCATION',
          pageName: 'pro-profile',
          userId,
          jobTypeId,
          prefecture: pro.location?.prefecture,
          city: pro.location?.city,
        });
      }

      dispatch(
        getCaseStudiesByUserId({
          userId: pro.id,
          jobTypeId: jobTypeId ?? '',
        })
      );
      dispatch(
        getAllCaseStudiesByUserId({
          userId: pro.id,
          jobTypeId: jobTypeId ?? '',
        })
      );
      await logicMiddleware.whenComplete();

      // Will be fixed in next.js#27003
      // https://github.com/vercel/next.js/pull/27003
      const encodedSlug = encodeURIComponent(pro.slug ?? '');

      // if pro has slug and userId is pro's id then we redirect to page using slug
      if (encodedSlug && userId === pro.id) {
        return {
          redirect: {
            destination: `${currentLocale === 'en' ? '/en' : ''}/profile/${encodedSlug}/pro`,
            permanent: true,
          },
        };
      }
      if (pro.isItp && pro.converted && pro.proId) {
        return {
          redirect: {
            destination: `${currentLocale === 'en' ? '/en' : ''}/profile/${pro.proId}/pro`,
            permanent: true,
          },
        };
      }
      if (
        encodedSlug &&
        pro.proProfile.customProfileUrl &&
        pro.slug !== pro.proProfile.customProfileUrl
      ) {
        if (req.url?.includes(encodedSlug)) {
          let big5JobType = '';
          for (const item of BIG_5_JOB_TYPE_LIST) {
            if (req.url.includes(item)) {
              big5JobType = '/home-reform-general';
            }
          }
          return {
            redirect: {
              destination: `${currentLocale === 'en' ? '/en' : ''}/profile/${encodeURIComponent(
                pro.proProfile.customProfileUrl
              )}/pro${big5JobType}`,
              permanent: true,
            },
          };
        }
      }

      if (jobTypeId && jobTypeId !== '') {
        const generalizeResult = await jobTypeAliasesService.getAlias(jobTypeId);
        if (generalizeResult) {
          const generalizedUrl = generalizeResult.alias;
          return {
            redirect: {
              destination: `${
                currentLocale === 'en' ? '/en' : ''
              }/profile/${encodedSlug}/pro/${generalizedUrl}`,
              permanent: false,
            },
          };
        }
      }

      if (
        encodedSlug &&
        userId === pro.id &&
        jobTypeId &&
        pro.enabledJobTypes.find((jobTypeItem) => jobTypeItem.id === jobTypeId)
      ) {
        return {
          redirect: {
            destination: `${
              currentLocale === 'en' ? '/en' : ''
            }/profile/${encodedSlug}/pro/${jobTypeId}`,
            permanent: true,
          },
        };
      }

      const seo: SeoObject = {};

      const isReviewsQAEnabled = await featureFlagsService.isReviewsQAEnabled();

      if (jobTypeId) {
        const result = await proProfileService.getSeoParameter(pro.id, jobTypeId);
        seo.noIndex = result?.isNoIndex ?? false;
        seo.canonicalUrl = result?.canonicalUrl ?? '';
      }

      if (!seo.noIndex) {
        seo.description = pro.seoDescription;
      }

      const questionAnswers: Array<QuestionAnswer> =
        pro.jobProfiles
          ?.find(
            (jobProfile: { jobTypeId: string | undefined }) =>
              jobProfile.jobTypeId === query.jobTypeId
          )
          ?.questionAnswers?.filter((questionAnswer) => questionAnswer.answer) ?? [];

      const featureFlags = { isReviewsQAEnabled };

      // case-studies
      let title = pro.name ? `${pro.name} | ${pro.location?.name ?? ''}` : '';
      if (jobTypeIdParam === SLUG_CASE_STUDIES || jobTypeIdParam === SLUG_REVIEWS) {
        const totalCaseStudies = caseStudiesTotalCountSelector(store.getState());
        const totalReviews = pro.reviews?.total ?? 0;

        if (
          (jobTypeIdParam === SLUG_CASE_STUDIES && totalCaseStudies < MIN_CASES) ||
          (jobTypeIdParam === SLUG_REVIEWS && totalReviews < MIN_CASES)
        ) {
          seo.noIndex = false;
        }

        const totalNumber = jobTypeIdParam === SLUG_CASE_STUDIES ? totalCaseStudies : totalReviews;

        // on accessing `/{slug}/pro/case-studies` or `/{slug}/pro/reviews` URL
        const locationTitle = pro.proProfile.location?.title ?? '';
        const companyName = pro.profile.companyNameKanji ?? '';
        const lastName = pro.profile.fullNameKanji?.last ?? '';
        const firstName = pro.profile.fullNameKanji?.first ?? '';

        const subject = pro.profile.isCompany ? companyName : `${lastName} ${firstName}`;

        const titleParam = jobTypeIdParam === SLUG_CASE_STUDIES ? '事例一覧' : '評判・口コミの一覧';

        title = `${subject} | ${locationTitle} | ${titleParam}｜ゼヒトモ`;

        const descriptionParam = jobTypeIdParam === SLUG_CASE_STUDIES ? '事例' : '評判・口コミ';

        seo.description = `${subject}（${locationTitle}）の${descriptionParam}です。ゼヒトモでは${subject}（${locationTitle}）の${descriptionParam}${totalNumber}件を掲載中。`;
      }

      let jobTypeTitle = '';

      if (jobTypeId && pro.jobProfiles) {
        jobTypeTitle = getJobTypeTitle(jobTypeId, pro.jobProfiles);
      }

      let internalBlogPostItems: Array<BlogPost> = [];
      if (jobTypeId) {
        internalBlogPostItems = await getInternalBlogPostsByJobTypeId({
          jobTypeId,
        });
      }
      let internalBlogCostPostItems: Array<BlogPost> = [];
      if (jobTypeId) {
        internalBlogCostPostItems = await getInternalBlogPostsByJobTypeId({
          isCostTag: true,
          jobTypeId,
        });
      }

      // If we have a jobTypeId selected, we only want the reviews for that specific job
      // Else, we want the data for all the reviews
      const targetRatings =
        (jobTypeId === null
          ? pro.reviews?.list
          : pro.reviews?.list.filter((review) => review.jobTypeId === jobTypeId)) ?? [];
      const ratingsCount = targetRatings.length;
      const ratingsAvg =
        ratingsCount > 0
          ? Number.parseFloat(
              (targetRatings.reduce((acc, cur) => acc + cur.rating, 0) / ratingsCount).toFixed(1)
            )
          : null;

      const localBusiness: ProLocalBusiness = {
        name: title,
        description: pro.seoDescription ?? pro.proProfile.description,
        image: pro.profile.avatar,
        reviewStatistics: ratingsAvg
          ? { ratingValue: ratingsAvg, reviewCount: ratingsCount }
          : null,
        address: {
          city: pro.location?.city?.name ?? '',
          townArea: pro.location?.townArea?.name,
          prefecture: pro.location?.prefecture?.name ?? '',
          zipCode: pro.location?.zipCode ?? '',
        },
      };

      const serverSideProps = {
        featureFlags,
        internalBlogPosts: internalBlogPostItems,
        internalBlogCostPosts: internalBlogCostPostItems,
        ...i18nData,
        jobProfiles: pro.jobProfiles,
        jobTypeId: jobTypeIdParam,
        jobTypeTitle,
        questionAnswers,
        seo,
        localBusiness,
        slugFromQueryParam: userId,
        title,
      };

      setSSRCache(cacheKeyProps, serverSideProps, 'props');
      setSSRCache(cacheKeyProps, getState(), 'states');

      return { props: serverSideProps };
    }
);

export default ProProfilePage;
